import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { axios } from '../api';
import { PaginationProps } from '../component/Pagination';
import { BOTTOM_TOAST, generateErrorMsg } from '../utils';

import { CustomerSearchParams } from '@/pages/CustomerLookupForm';

export interface FilterParams {
  [key: string]: string | number | boolean | string[] | undefined;
}

type Pagination = {
  count: number;
  next: string | null;
  previous: string | null;
};
export interface TResponseWithPagination<T> extends Pagination {
  results: T[];
}

type Props<T> = {
  endpoint: string;
  mapper: (element: T) => any;
  payload?: CustomerSearchParams;
  pageSize?: number;
};

const VISIBLE_PAGES_LENGTH_FOR_SELECT = 5;

export const useFetchWithPagination = <T>({
  endpoint,
  payload,
  mapper,
  pageSize = 25,
}: Props<T>) => {
  const [data, setData] = useState<T[] | null>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationObject, setPaginationObject] = useState<Pagination>({
    next: null,
    previous: null,
    count: 0,
  });
  const [searchField, setSearchField] = useState<string | undefined>();
  const [searchText, setSearchText] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const renderPagination = useMemo(() => paginationObject.count > 10, [paginationObject.count]);
  const totalPages = useMemo(() => {
    return Math.ceil(paginationObject.count / pageSize);
  }, [pageSize, paginationObject.count]);

  const pages = useMemo(() => {
    const start =
      Math.floor((currentPage - 1) / VISIBLE_PAGES_LENGTH_FOR_SELECT) *
      VISIBLE_PAGES_LENGTH_FOR_SELECT;
    const end =
      start + VISIBLE_PAGES_LENGTH_FOR_SELECT > totalPages
        ? totalPages
        : start + VISIBLE_PAGES_LENGTH_FOR_SELECT;
    return Array.from({ length: end - start }, (_, i) => start + i + 1);
  }, [currentPage, totalPages]);

  const setPageSearchParam = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        if (page <= 1) {
          const params = new URLSearchParams(prev);
          params.delete('page');
          return params;
        } else {
          const params = new URLSearchParams(prev);
          params.set('page', page.toString());
          return params;
        }
      });
    },
    [setSearchParams]
  );

  const fetch = (requestUrl?: string, params: { page?: number } = {}) => {
    setIsLoading(true);
    const apiRequest = requestUrl ? requestUrl : endpoint;
    let response;
    if (apiRequest.includes('customer_search') && !!payload) {
      response = axios.post(apiRequest, payload, {
        params,
      });
    } else {
      response = axios.get(apiRequest, {
        params: {
          ...(searchField ? { [searchField]: searchText } : undefined),
          ...params,
        },
      });
    }

    response
      .then((response) => {
        const { next, count, previous, results } = response.data as TResponseWithPagination<T>;
        setData(results.map((user: T) => mapper(user)));
        setPaginationObject({ next, previous, count });
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      });
  };

  useEffect(() => {
    const pageFromSearchParams = Number(searchParams.get('page')) || 1;
    fetch(endpoint, { page: pageFromSearchParams });
    setCurrentPage(pageFromSearchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = useCallback(
    (page: number) => {
      setCurrentPage(page);
      setPageSearchParam(page);
      fetch(endpoint, { page });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpoint, setPageSearchParam]
  );

  const goNext = useCallback(() => {
    if (paginationObject.next) {
      const nextPage = currentPage + 1;
      setCurrentPage((prev) => prev + 1);
      setPageSearchParam(nextPage);
      fetch(endpoint, { page: nextPage });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, paginationObject.next, endpoint]);

  const goPrev = useCallback(() => {
    if (paginationObject.previous) {
      const prevPage = currentPage - 1;
      setCurrentPage((prev) => prev - 1);
      setPageSearchParam(prevPage);
      fetch(endpoint, { page: prevPage });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, paginationObject.previous, endpoint]);

  const searchBy = (text: string, field: string) => {
    setSearchField(field);
    setSearchText(text);
    setCurrentPage(1);
    setPageSearchParam(1);
    fetch(endpoint, { [field]: text });
  };

  const reFetch = () => {
    setSearchText(undefined);
    fetch(endpoint, { [searchField as string]: undefined });
    setCurrentPage(1);
    setPageSearchParam(1);
  };

  return {
    currentData: data,
    currentPage,
    canGo: {
      next: !!paginationObject.next,
      previous: !!paginationObject.previous,
    },
    pages,
    goTo,
    goNext,
    goPrev,
    renderPagination,
    searchBy,
    searchText,
    searchField,
    countOfResults: paginationObject.count,
    reFetch,
    isLoading,
  } as PaginationProps & {
    currentData: any;
    renderPagination: boolean;
    searchBy: (t: string, field: string) => void;
    searchText: string | undefined;
    searchField: string | undefined;
    countOfResults: number;
    reFetch: () => void;
    isLoading: boolean;
  };
};

export const useFetchWithFilters = <T>({
  endpoint,
  payload,
  mapper,
  pageSize = 25,
  initialFilters = {},
}: Props<T> & { initialFilters?: FilterParams }) => {
  const [data, setData] = useState<T[] | null>([]);
  const [filters, setFilters] = useState<FilterParams>(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationObject, setPaginationObject] = useState<Pagination>({
    next: null,
    previous: null,
    count: 0,
  });
  const [searchField, setSearchField] = useState<string | undefined>();
  const [searchText, setSearchText] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Use ref to avoid stale closure issues with fetch in useCallback
  const fetchRef = useRef<typeof fetch>();

  const renderPagination = useMemo(() => paginationObject.count > 10, [paginationObject.count]);
  const totalPages = useMemo(() => {
    return Math.ceil(paginationObject.count / pageSize);
  }, [pageSize, paginationObject.count]);

  const pages = useMemo(() => {
    const start =
      Math.floor((currentPage - 1) / VISIBLE_PAGES_LENGTH_FOR_SELECT) *
      VISIBLE_PAGES_LENGTH_FOR_SELECT;
    const end =
      start + VISIBLE_PAGES_LENGTH_FOR_SELECT > totalPages
        ? totalPages
        : start + VISIBLE_PAGES_LENGTH_FOR_SELECT;
    return Array.from({ length: end - start }, (_, i) => start + i + 1);
  }, [currentPage, totalPages]);

  const buildQueryParams = useCallback(
    (
      params: { page?: number } = {},
      overrideFilters?: FilterParams,
      overrideSearchText?: string,
      overrideSearchField?: string
    ) => {
      const queryParams: any = { ...params };

      // Add search parameter (use override if provided, otherwise use state)
      const currentSearchField = overrideSearchField || searchField;
      const currentSearchText = overrideSearchText !== undefined ? overrideSearchText : searchText;
      if (currentSearchField && currentSearchText) {
        queryParams[currentSearchField] = currentSearchText;
      }

      // Add filter parameters (use overrideFilters if provided, otherwise use state)
      const currentFilters = overrideFilters || filters;
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            // For multi-select filters, join with comma
            queryParams[key] = value.join(',');
          } else {
            queryParams[key] = value;
          }
        }
      });

      return queryParams;
    },
    [filters, searchField, searchText]
  );

  const setPageSearchParam = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        if (page <= 1) {
          const params = new URLSearchParams(prev);
          params.delete('page');
          return params;
        } else {
          const params = new URLSearchParams(prev);
          params.set('page', page.toString());
          return params;
        }
      });
    },
    [setSearchParams]
  );

  const fetch = (
    requestUrl?: string,
    params: { page?: number } = {},
    overrideFilters?: FilterParams,
    overrideSearchText?: string,
    overrideSearchField?: string
  ) => {
    // Update ref to avoid stale closures in useCallback
    fetchRef.current = fetch;

    setIsLoading(true);
    const apiRequest = requestUrl ? requestUrl : endpoint;
    let response;

    const queryParams = buildQueryParams(
      params,
      overrideFilters,
      overrideSearchText,
      overrideSearchField
    );

    if (apiRequest.includes('customer_search') && !!payload) {
      response = axios.post(apiRequest, payload, {
        params: queryParams,
      });
    } else {
      response = axios.get(apiRequest, {
        params: queryParams,
      });
    }

    response
      .then((response) => {
        const { next, count, previous, results } = response.data as TResponseWithPagination<T>;
        setData(results.map((user: T) => mapper(user)));
        setPaginationObject({ next, previous, count });
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      });
  };

  useEffect(() => {
    const pageFromSearchParams = Number(searchParams.get('page')) || 1;
    fetch(endpoint, { page: pageFromSearchParams }, filters);
    setCurrentPage(pageFromSearchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = useCallback(
    (page: number) => {
      setCurrentPage(page);
      setPageSearchParam(page);
      fetchRef.current?.(endpoint, { page }, filters);
    },
    [endpoint, filters, setPageSearchParam]
  );

  const goNext = useCallback(() => {
    if (paginationObject.next) {
      const nextPage = currentPage + 1;
      setCurrentPage((prev) => prev + 1);
      setPageSearchParam(nextPage);
      fetchRef.current?.(endpoint, { page: nextPage }, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, paginationObject.next, endpoint, filters]);

  const goPrev = useCallback(() => {
    if (paginationObject.previous) {
      const prevPage = currentPage - 1;
      setCurrentPage((prev) => prev - 1);
      setPageSearchParam(prevPage);
      fetchRef.current?.(endpoint, { page: prevPage }, filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, paginationObject.previous, endpoint, filters]);

  const searchBy = (text: string, field: string) => {
    setSearchField(field);
    setSearchText(text);
    setCurrentPage(1);
    setPageSearchParam(1);
    fetchRef.current?.(endpoint, { [field]: text }, filters);
  };

  const applyFilters = useCallback(
    (newFilters: FilterParams, searchText?: string, searchField?: string) => {
      setFilters(newFilters);
      setCurrentPage(1);
      setPageSearchParam(1);
      // Trigger fetch with new filters and search parameters
      fetchRef.current?.(endpoint, {}, newFilters, searchText, searchField || 'search');
    },
    [endpoint, setPageSearchParam]
  );

  const clearFilters = useCallback(() => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    setSearchField(undefined);
    setSearchText(undefined);
    setCurrentPage(1);
    setPageSearchParam(1);
    fetchRef.current?.(endpoint, {}, emptyFilters);
  }, [endpoint, setPageSearchParam]);

  const reFetch = () => {
    setSearchText(undefined);
    fetchRef.current?.(endpoint, { [searchField as string]: undefined }, filters);
    setCurrentPage(1);
    setPageSearchParam(1);
  };

  return {
    currentData: data,
    currentPage,
    canGo: {
      next: !!paginationObject.next,
      previous: !!paginationObject.previous,
    },
    pages,
    goTo,
    goNext,
    goPrev,
    renderPagination,
    searchBy,
    applyFilters,
    clearFilters,
    filters,
    searchText,
    searchField,
    countOfResults: paginationObject.count,
    reFetch,
    isLoading,
  } as PaginationProps & {
    currentData: any;
    renderPagination: boolean;
    searchBy: (t: string, field: string) => void;
    applyFilters: (filters: FilterParams, searchText?: string, searchField?: string) => void;
    clearFilters: () => void;
    filters: FilterParams;
    searchText: string | undefined;
    searchField: string | undefined;
    countOfResults: number;
    reFetch: () => void;
    isLoading: boolean;
  };
};
